import React, { useEffect, useState, useMemo } from "react";
import {
  DatePicker, Timeline, Select, Spin, Empty, Pagination,
} from "antd";
import dayjs from "dayjs";

import { toast } from "react-toastify";
import { fetchActivity } from "../../../../actions/dashboard";
import CustomDebounceSearch from "../../../Common/ChemBioPending/Components/CustomDebounceSearch";
import useAbortController from "../../../Common/hooks/useAbortController";
import { formatAntdDateRange } from "../../../../utils/helpers";

import "./Activity.css";

const { RangePicker } = DatePicker;

const DefaultActivityOption = [
  { value: "", label: "All" },
];
const getDefaultDateRange = () => [dayjs(), dayjs()];
const DEFAULT_PAGE_SIZE = 1000;
const PAGE_SIZE_OPTIONS = ["500", "1000", "1500", "2000"];

export default function Activity() {
  const [activityData, setActivityData] = useState([]);
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [selectedType, setSelectedType] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [activityOptions, setActivityOptions] = useState(DefaultActivityOption);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const { setNewAbortController } = useAbortController();
  /**
 * Formats the given data into a specific structure for display.
 * @param {Array<Object>} data - The data to be formatted.
 * @return {Array<Object>} formattedData - The formatted data.
 */
  const formatData = (data) => data.map((item) => ({
    label: (
      <div className="Dashboard__Activity__Timeline__LabelDiv">
        <div className="Dashboard__Activity__Timeline__LabelDiv__Actor"><span title={item.actor}>{item.actor}</span></div>
        <span>{dayjs(`${item.date}, ${item.time}`).format("MM-DD-YY, hh:mm:ss A")}</span>
      </div>
    ),
    children: (
      <div className="Dashboard__Activity__Card__Body">
        {item.update_type}
        <br />
        {item.sentence}
      </div>
    ),
  }));

  const paginatedActivityData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return activityData.slice(startIndex, startIndex + pageSize);
  }, [activityData, currentPage, pageSize]);

  /**
   * Fetches and updates the activity data based on the selected date range and stage filter.
   */
  const getActivity = async () => {
    setLoading(true);
    const newAbortController = setNewAbortController();
    const payload = {
      date_range: formatAntdDateRange(dateRange), update_type: selectedType, search: searchValue,
    };
    const {
      success, results, message, logs_update_types, canceled,
    } = await fetchActivity(payload, newAbortController.signal);
    if (success) {
      const formattedData = formatData(results ?? []);
      setActivityOptions(logs_update_types.map((item) => ({ value: item.update_type || "", label: item.display_name })));
      setActivityData(formattedData);
    } else if (!canceled) {
      toast.error(message);
    }
    setLoading(canceled === undefined ? false : canceled);
  };

  /**
   * Fetches the activity data based on the selected date range and stage filter.
   */
  useEffect(() => {
    getActivity();
  }, [dateRange, selectedType, searchValue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentPage(1);
  }, [dateRange, selectedType, searchValue]);

  const scrollPageToTop = () => {
    window.scrollTo(0, 0);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    scrollPageToTop();
  };

  const handlePageSizeChange = (_current, nextPageSize) => {
    setPageSize(nextPageSize);
    setCurrentPage(1);
    scrollPageToTop();
  };

  const onSearch = (e) => {
    const value = e.target.value;
    if (value === searchValue) {
      getActivity(); // If the search value does not change, then trigger the API function directly instead of setting the searchValue state
    } else {
      setSearchValue(value);
    }
  };

  return (
    <div className="Dashboard__Activity__Container">
      <div className="Dashboard__Activity__Filters__Container">
        <CustomDebounceSearch placeholder="Search by Workorder, Genista ID" onSearch={onSearch} />
        <div className="Dashboard__Activity__FilterDiv">
          <label>Workorder Date</label>
          <RangePicker
            allowClear
            className="Dashboard__Activity__DatePicker"
            format="MM/DD/YYYY"
            value={dateRange}
            onChange={(value) => setDateRange(value ?? getDefaultDateRange())}
          />
        </div>
        <div className="Dashboard__Activity__FilterDiv">
          <label>Activity</label>
          <Select
            showSearch
            allowClear
            className="Dashboard__Activity__Select"
            loading={loading}
            placeholder="Activity"
            defaultValue={activityOptions[0].label}
            value={selectedType}
            options={activityOptions}
            onSelect={(type) => setSelectedType(type)}
            onClear={() => setSelectedType("")}
            filterOption={(inputValue, option) => option.label?.toLowerCase().startsWith(inputValue?.toLowerCase())}
          />
        </div>
      </div>
      <Spin spinning={loading} size="large">
        {activityData.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
        {activityData.length !== 0 && (
          <>
            <div className="Dashboard__Activity__Timeline__Container">
              <Timeline
                mode="left"
                className="Dashboard__Activity__Timeline"
                items={paginatedActivityData}
              />
            </div>
            <Pagination
              className="Dashboard__Activity__Pagination"
              current={currentPage}
              pageSize={pageSize}
              total={activityData.length}
              showSizeChanger
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onChange={handlePageChange}
              onShowSizeChange={handlePageSizeChange}
            />
          </>
        )}
      </Spin>
    </div>
  );
}
